import React, { useState, useEffect } from 'react';
import TaskCard from './components/TaskCard';
import TaskModal from './components/TaskModal';
import FilterSidebar from './components/FilterSidebar';
import UserStats from './components/UserStats';
import { Plus } from 'lucide-react';

function App() {
  const [tasks, setTasks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filters, setFilters] = useState({
    priority: 'all',
    status: 'all',
    tags: [],
    sortBy: 'dueDate'
  });


  const [userStats, setUserStats] = useState({
    level: 1,
    xp: 0,
    maxXp: 100,
    health: 50,
    maxHealth: 50,
    coins: 0
  });

  // Load stats from localStorage
  useEffect(() => {
    const savedStats = localStorage.getItem('ai-user-stats');
    if (savedStats) {
      setUserStats(JSON.parse(savedStats));
    }
  }, []);

  // Save stats to localStorage
  useEffect(() => {
    localStorage.setItem('ai-user-stats', JSON.stringify(userStats));
  }, [userStats]);

  // Load tasks from localStorage on mount
  useEffect(() => {
    const savedTasks = localStorage.getItem('ai-tasks');
    if (savedTasks) {
      setTasks(JSON.parse(savedTasks));
    }
  }, []);

  // Save tasks to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('ai-tasks', JSON.stringify(tasks));
  }, [tasks]);

  const createTask = (taskData) => {
    const newTask = {
      id: Date.now().toString(),
      ...taskData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setTasks([...tasks, newTask]);
    setIsModalOpen(false);
    showToast('Task created successfully!');
  };

  const updateTask = (id, taskData) => {
    setTasks(tasks.map(task =>
      task.id === id
        ? { ...task, ...taskData, updatedAt: new Date().toISOString() }
        : task
    ));
    setEditingTask(null);
    setIsModalOpen(false);
    showToast('Task updated successfully!');

  };

  const checkLevelUp = (currentStats) => {
    let { level, xp, maxXp, health, maxHealth } = currentStats;

    if (xp >= maxXp) {
      level += 1;
      xp = xp - maxXp;
      maxXp = Math.floor(maxXp * 1.5);
      health = maxHealth; // Heal on level up
      showToast(`🎉 Level Up! You are now Level ${level}!`);
    }

    return { ...currentStats, level, xp, maxXp, health };
  };

  const handleTaskCompletion = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    // Calculate rewards based on difficulty/priority
    let xpGain = 10;
    let coinsGain = 5;

    if (task.priority === 'high') {
      xpGain = 20;
      coinsGain = 10;
    } else if (task.priority === 'medium') {
      xpGain = 15;
      coinsGain = 8;
    }

    setUserStats(prev => {
      const newStats = {
        ...prev,
        xp: prev.xp + xpGain,
        coins: prev.coins + coinsGain
      };
      return checkLevelUp(newStats);
    });

    // Update task status based on type
    if (task.type === 'habit') {
      const newStreak = (task.streak || 0) + 1;
      updateTask(taskId, { streak: newStreak });
      showToast(`Habit +1! Streak: ${newStreak}`);
    } else {
      updateTask(taskId, { status: 'completed' });
    }
  };

  const deleteTask = (id) => {
    setTasks(tasks.filter(task => task.id !== id));
    showToast('Task deleted!');
  };

  const showToast = (message) => {
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  };

  const openEditModal = (task) => {
    setEditingTask(task);
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setEditingTask(null);
    setIsModalOpen(true);
  };

  // Filter and sort tasks
  const getFilteredTasks = () => {
    let filtered = [...tasks];

    // Filter by priority
    if (filters.priority !== 'all') {
      filtered = filtered.filter(task => task.priority === filters.priority);
    }

    // Filter by status
    if (filters.status !== 'all') {
      filtered = filtered.filter(task => task.status === filters.status);
    }

    // Filter by tags
    if (filters.tags.length > 0) {
      filtered = filtered.filter(task =>
        task.tags?.some(tag => filters.tags.includes(tag))
      );
    }

    // Sort tasks
    filtered.sort((a, b) => {
      switch (filters.sortBy) {
        case 'dueDate':
          return new Date(a.dueDate || '9999-12-31') - new Date(b.dueDate || '9999-12-31');
        case 'priority':
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        case 'title':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    return filtered;
  };

  const filteredTasks = getFilteredTasks();

  return (
    <div className="min-h-screen bg-dark-bg">
      {/* Header */}
      <header className="border-b border-white/5 backdrop-blur-sm sticky top-0 z-50 bg-dark-bg/80">
        <div className="max-w-[1100px] mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-poppins font-semibold bg-gradient-primary bg-clip-text text-transparent">
                AI Task Manager
              </h1>
              <p className="text-white/60 mt-1 text-sm">
                Smart task management with AI-powered improvements
              </p>
            </div>
            <button
              onClick={openCreateModal}
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={20} />
              New Task
            </button>
          </div>
        </div>
      </header>



      {/* Main Content */}
      <div className="max-w-[1100px] mx-auto px-6 py-8">
        <UserStats stats={userStats} />

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Filter Sidebar */}
          <FilterSidebar
            filters={filters}
            setFilters={setFilters}
            tasks={tasks}
          />

          {/* Tasks Grid */}
          <div>
            {filteredTasks.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-white/30 mb-4">
                  <svg className="mx-auto h-24 w-24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <h3 className="text-xl text-white/70 mb-2">No tasks yet</h3>
                <p className="text-white/50 mb-6">Create your first task to get started</p>
                <button onClick={openCreateModal} className="btn-primary">
                  Create Task
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredTasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onEdit={openEditModal}
                    onDelete={deleteTask}

                    onUpdate={updateTask}
                    onComplete={() => handleTaskCompletion(task.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Task Modal */}
      {
        isModalOpen && (
          <TaskModal
            task={editingTask}
            onSave={editingTask ? updateTask : createTask}
            onClose={() => {
              setIsModalOpen(false);
              setEditingTask(null);
            }}
          />
        )
      }
    </div >
  );
}

export default App;
